import React, {FC, useEffect, useState, useCallback} from "react"
import {FormattedMessage, injectIntl} from "@irati/utils/lang"
import {WellnessMediaCategory} from "@irati/types"
import {H3} from "@irati/ui/src/Typography"
import {DESKTOP, PHONE_LANDSCAPE, TABLET} from "@irati/ui/src/global/media"
import wellnessService from "@irati/wellness/services/Wellness"
import Slider from "@irati/ui/src/Slider"
import Spacing from "@irati/ui/src/Spacing"
import Flex from "@irati/ui/src/Flex"
import tracking from "@irati-tracking/wellness"
import {SEE_ALL} from "@irati/wellness/utils"
import History from "@irati/utils/history"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {RouterLink} from "@irati/ui/src/Link"
import authentication from "@irati/auth"
import productSettingsService from "@irati/permissions/services/productSettings"
import devToggles from "@irati/dev-toggles"
import {getIconColor, getSelectorColor} from "../../utils"
import {WellnessCategories} from "./WellnessCategories"
import {ShowMore} from "./ShowMore"
import {StyledSelector, WellnessCategoriesWrapper} from "./styled"

interface Props {
  categoryId?: string
  showLink?: boolean
  isActive?: boolean
  onClick?: (val: string, categoryName: string) => void
  selectedCategoryIds?: string[]
  onCategoryToggle?: (categoryId: string) => void
}

const sliderSettings = {
  slidesToShow: 6,
  slidesToScroll: 2,
  responsive: [
    {breakpoint: PHONE_LANDSCAPE, settings: {slidesToShow: 1.5, slidesToScroll: 1.5}},
    {breakpoint: TABLET, settings: {slidesToShow: 4, slidesToScroll: 1}},
    {breakpoint: DESKTOP - 1, settings: {slidesToShow: 2.2, slidesToScroll: 2}}
  ]
}

export const WELLNESS_MEDIA_ALL_CATEGORY: WellnessMediaCategory = {
  uuid: "all",
  id: "all",
  name: "All",
  count: 0,
  svgData: "all",
  mediaLabel: "all"
}

const WellnessCategoryList: FC<Props> = ({showLink, onClick, isActive, categoryId, selectedCategoryIds, onCategoryToggle}) => {
  const [wellnessCategoryList, setWellnessCategoryList] = useState<WellnessMediaCategory[]>([])
  const [showSeeAll, setShowSeeAll] = useState<boolean>(showLink ?? false)
  const [isShowingMore, setIsShowingMore] = useState<boolean>(false)
  const [displayShowMoreText, setDisplayShowMoreText] = useState<boolean>(true)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(categoryId || null)
  const [visibleCount, setVisibleCount] = useState<number>(6) // Default for desktop

  const isMultiSelectMode = selectedCategoryIds !== undefined && onCategoryToggle !== undefined

  useEffect(() => {
    const loadData = async () => {
      const business = authentication.business()

      const [wellnessCategories, productSettings] = await Promise.all([
        wellnessService.getWellnessMediaCategories(),
        productSettingsService.get(business.uuid, business.rootTeamId)
      ])

      const blacklistedCategoryIds = (productSettings.wellnessSettings?.blacklistedCategories || []).map((cat) => cat.id)

      const filteredCategories = wellnessCategories.filter((category) => {
        const catId = category.uuid || category.id
        return !blacklistedCategoryIds.includes(catId)
      })

      if (isMultiSelectMode) {
        setWellnessCategoryList([WELLNESS_MEDIA_ALL_CATEGORY, ...filteredCategories])
      } else {
        setWellnessCategoryList(filteredCategories)
      }
    }

    loadData()
  }, [isMultiSelectMode])

  useEffect(() => {
    setSelectedCategory(categoryId || null)
  }, [categoryId])

  const getVisibleCategoryCount = () => {
    const width = window.innerWidth
    if (width < PHONE_LANDSCAPE) return 1
    if (width < TABLET) return 4
    if (width < DESKTOP) return 2
    return 6
  }

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleCategoryCount())
    }

    setVisibleCount(getVisibleCategoryCount())

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const shouldDisplayShowMoreText = () => {
    return wellnessCategoryList.length > visibleCount
  }

  const trackingSellAll = () => {
    tracking.viewCategorySelected(SEE_ALL)
    setShowSeeAll(false)
  }

  const handleSelectCategory = useCallback(
    (uuid: string, name: string) => {
      if (isMultiSelectMode && onCategoryToggle) {
        onCategoryToggle(uuid)
        tracking.viewCategorySelected(name)
      } else if (onClick) {
        onClick(uuid, name)
        tracking.viewCategorySelected(name)
        History.push({pathname: `/wellness/categories/${uuid}`})
        setSelectedCategory(uuid)
      }
    },
    [onClick, onCategoryToggle, isMultiSelectMode]
  )

  const handleShowMoreClick = () => {
    if (!isShowingMore) {
      tracking.wellnesShowMoreCategories()
    }
    setIsShowingMore((prev) => !prev)
  }

  return (
    <>
      <Flex direction="row" marginTop={isMultiSelectMode ? -2 : 5} justifyContent="space-between" alignItems="center">
        <Flex>
          <H3>
            {isMultiSelectMode ? <FormattedMessage id="wellness.search.filters" /> : <FormattedMessage id="wellness.categories.title" />}
          </H3>
        </Flex>
        {!devToggles.showNewWellness() && showSeeAll && (
          <RouterLink cta to="/categories/" onClick={trackingSellAll}>
            <Flex alignItems="center">
              <FormattedMessage id="wellness.seeAll" />
              <Icon icon={IconTypes.arrowRight} marginLeft />
            </Flex>
          </RouterLink>
        )}
      </Flex>

      {devToggles.showNewWellness() ? (
        <>
          <Spacing marginTop={isMultiSelectMode ? -7 : 2} />
          <WellnessCategoriesWrapper>
            <WellnessCategories
              isShowingMore={isShowingMore}
              displayShowMoreText={displayShowMoreText}
              shouldDisplayShowMoreText={shouldDisplayShowMoreText}
              data={wellnessCategoryList}
              selectedCategoryIds={isMultiSelectMode ? selectedCategoryIds : categoryId ? [categoryId] : []}
              handleCategoryClick={handleSelectCategory}
            />
            {shouldDisplayShowMoreText() && (
              <ShowMore
                isShowingMore={isShowingMore}
                handleShowMoreClick={handleShowMoreClick}
                displayShowMoreText={displayShowMoreText}
              />
            )}
          </WellnessCategoriesWrapper>
        </>
      ) : (
        <Slider settings={sliderSettings}>
          {wellnessCategoryList?.map((category, key) => (
            <Spacing key={category.uuid} display="inline-block" marginTop={2} paddingRight={0.5} paddingLeft={0.5}>
              <StyledSelector
                name={category.name}
                uuid={category.uuid}
                svgData={category.svgData}
                selectorBgColor={getSelectorColor(key)}
                iconBgColor={getIconColor(key)}
                count={category.count}
                selected={isActive && category.uuid === categoryId}
                onClick={() => handleSelectCategory(category.uuid, category.name)}
              />
            </Spacing>
          ))}
        </Slider>
      )}
    </>
  )
}

export default injectIntl(WellnessCategoryList)
